import React, { useState, useMemo } from 'react';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CircularProgress from '@mui/material/CircularProgress';
import { useConceptGate } from '../../context/ConceptGateContext';
import { Direction } from '../../api/conceptgate';
import './MobileGateOverlay.css';

const MobileGateOverlay = ({ onNavigateToGate, selectedInstrument, selectedCategory }) => {
  const {
    currentTrade,
    lastResult: validationResult,
    validating: isValidating,
    streamingProgress,
    validateStreaming,
    account,
    setSelectedInstrument,
  } = useConceptGate();

  // Sync instrument to context when it changes
  React.useEffect(() => {
    if (selectedInstrument && setSelectedInstrument) {
      setSelectedInstrument(selectedInstrument, selectedCategory);
    }
  }, [selectedInstrument, selectedCategory, setSelectedInstrument]);

  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate trade metrics
  const calculations = useMemo(() => {
    if (!currentTrade || !currentTrade.entryPrice || !currentTrade.stopPrice) {
      return { stopPips: null, rr: null, lotSize: null };
    }

    const entry = parseFloat(currentTrade.entryPrice);
    const stop = parseFloat(currentTrade.stopPrice);
    const target = currentTrade.targetPrice ? parseFloat(currentTrade.targetPrice) : null;

    const isJpyPair = currentTrade.instrument?.includes('JPY');
    const isCrypto = currentTrade.instrument?.endsWith('USDT') ||
                     currentTrade.instrument?.endsWith('BTC');

    let pipMultiplier = 10000;
    if (isJpyPair) pipMultiplier = 100;
    else if (isCrypto) pipMultiplier = 100;

    const stopPips = Math.abs(entry - stop) * pipMultiplier;
    const targetPips = target ? Math.abs(target - entry) * pipMultiplier : null;
    const rr = targetPips && stopPips > 0 ? targetPips / stopPips : null;

    // Calculate lot size
    const equity = account?.equity || 100000;
    const riskPercent = currentTrade.riskPercent || 1;
    const riskDollar = equity * (riskPercent / 100);
    const unitsPerLot = isCrypto ? 1 : 100000;
    const priceDist = Math.abs(entry - stop);
    const lotSize = priceDist > 0 ? riskDollar / (priceDist * unitsPerLot) : null;

    return {
      stopPips: Math.round(stopPips * 10) / 10,
      rr,
      lotSize,
    };
  }, [currentTrade, account]);

  // Determine status
  const getStatus = () => {
    if (isValidating) return 'validating';
    if (!validationResult || !validationResult.status) return 'none';
    return validationResult.status === 'pass' ? 'go' : 'nogo';
  };

  const status = getStatus();
  const isLong = currentTrade?.direction === Direction.LONG;

  const handleRunValidation = (e) => {
    e.stopPropagation();
    if (!isValidating && currentTrade) {
      validateStreaming();
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (onNavigateToGate) {
      onNavigateToGate();
    }
  };

  // Don't render if no trade
  if (!currentTrade) {
    return (
      <div className="mobile-gate-overlay empty" onClick={onNavigateToGate}>
        <FactCheckOutlinedIcon className="overlay-icon" />
        <span className="overlay-empty-text">Tap to add trade setup</span>
      </div>
    );
  }

  return (
    <div className={`mobile-gate-overlay ${isExpanded ? 'expanded' : ''}`}>
      {/* Compact Header - Always Visible */}
      <div className="overlay-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="overlay-trade-info">
          <span className="overlay-instrument">{currentTrade.instrument || 'EURUSD'}</span>
          <span className={`overlay-direction ${isLong ? 'long' : 'short'}`}>
            {isLong ? <TrendingUpIcon fontSize="inherit" /> : <TrendingDownIcon fontSize="inherit" />}
            {isLong ? 'L' : 'S'}
          </span>
        </div>

        <div className="overlay-stats">
          <span className="overlay-stat">
            <span className="stat-label">SL</span>
            <span className="stat-value">{calculations.stopPips || '-'}</span>
          </span>
          <span className="overlay-stat">
            <span className="stat-label">RR</span>
            <span className="stat-value highlight">{calculations.rr?.toFixed(1) || '-'}</span>
          </span>
        </div>

        <div className="overlay-status-section">
          {status === 'validating' ? (
            <div className="overlay-validating">
              <CircularProgress size={14} thickness={4} />
              {streamingProgress && (
                <span className="progress-text">
                  {streamingProgress.current}/{streamingProgress.total}
                </span>
              )}
            </div>
          ) : (
            <span className={`overlay-status-badge ${status}`}>
              {status === 'go' ? 'GO' : status === 'nogo' ? 'NO-GO' : '-'}
            </span>
          )}
        </div>

        <div className="overlay-actions">
          <button
            className="overlay-btn run"
            onClick={handleRunValidation}
            disabled={isValidating}
            aria-label="Run validation"
          >
            <PlayArrowIcon fontSize="small" />
          </button>
          <button
            className="overlay-btn edit"
            onClick={handleEdit}
            aria-label="Edit trade"
          >
            <EditOutlinedIcon fontSize="small" />
          </button>
          <button
            className="overlay-btn expand"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
          >
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="overlay-details">
          <div className="overlay-detail-row">
            <span className="detail-label">Entry</span>
            <span className="detail-value">{parseFloat(currentTrade.entryPrice).toFixed(5)}</span>
          </div>
          <div className="overlay-detail-row">
            <span className="detail-label">Stop</span>
            <span className="detail-value stop">{parseFloat(currentTrade.stopPrice).toFixed(5)}</span>
          </div>
          {currentTrade.targetPrice && (
            <div className="overlay-detail-row">
              <span className="detail-label">Target</span>
              <span className="detail-value target">{parseFloat(currentTrade.targetPrice).toFixed(5)}</span>
            </div>
          )}
          <div className="overlay-detail-row">
            <span className="detail-label">Size</span>
            <span className="detail-value">{calculations.lotSize?.toFixed(2) || '-'} lots</span>
          </div>

          {/* Quick validation summary */}
          {validationResult && validationResult.gates && (
            <div className="overlay-gates">
              {validationResult.gates.map((gate, idx) => {
                // Map status: 'pass' -> 'passed', 'fail' -> 'failed'
                const chipStatus = gate.status === 'pass' ? 'passed'
                  : gate.status === 'fail' ? 'failed'
                  : gate.status || 'pending';
                return (
                  <span
                    key={idx}
                    className={`overlay-gate-chip ${chipStatus}`}
                    title={gate.message}
                  >
                    {gate.ruleName?.split(' ')[0] || `Gate ${idx + 1}`}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileGateOverlay;
